/**
 * Vike-backed react-router-dom shim.
 *
 * Existing page/component code imports react-router-dom. Vite aliases those
 * imports here so we keep page components unchanged while Vike owns routing.
 * Vitest keeps the real react-router-dom (no alias in vitest.config.ts).
 *
 * ## Supported APIs (production call sites audited Jul 2026)
 * - useParams, useLocation, useNavigate, useSearchParams
 * - Link, Navigate
 * - Outlet / OutletProvider / useOutlet (flat single level — Vike layouts use children)
 * - useMatches (synthetic match from pageContext; handle.bare from /lp/ prefix)
 *
 * ## Test-only stubs (real RR under vitest)
 * - MemoryRouter, BrowserRouter, Routes, Route, createBrowserRouter, RouterProvider
 *
 * Shared libs (storefront-funnel/campaign/shared, afrodita) do NOT import react-router-dom.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
  type AnchorHTMLAttributes,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { usePageContext } from 'vike-react/usePageContext';

type NavigationTarget = string | { pathname?: string; search?: string; hash?: string };

export type UIMatch = {
  id: string;
  pathname: string;
  pathnameBase: string;
  params: Record<string, string | undefined>;
  data: unknown;
  handle: { bare?: boolean } | undefined;
};

function resolveTo(to: NavigationTarget): string {
  if (typeof to === 'string') return to;
  const pathname = to.pathname ?? '/';
  const search = to.search
    ? to.search.startsWith('?')
      ? to.search
      : `?${to.search}`
    : '';
  const hash = to.hash
    ? to.hash.startsWith('#')
      ? to.hash
      : `#${to.hash}`
    : '';
  return `${pathname}${search}${hash}`;
}

/** Client-only navigate — never statically import vike/client/router (SSR-unsafe). */
function clientNavigate(href: string, opts?: { overwriteLastHistoryEntry?: boolean }): void {
  if (import.meta.env.SSR || typeof window === 'undefined') return;
  void import('vike/client/router').then(({ navigate }) => {
    void navigate(href, opts);
  });
}

/**
 * True when Link should SPA-navigate (same-app). External absolute URLs,
 * mailto/tel, protocol-relative, and hash-only hrefs use the browser default.
 */
export function shouldClientNavigate(href: string): boolean {
  if (!href || href.startsWith('#')) return false;
  // Absolute or scheme-based URLs (http:, mailto:, tel:, //cdn…)
  if (/^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith('//')) {
    if (typeof window === 'undefined') return false;
    try {
      const url = new URL(href, window.location.href);
      return url.origin === window.location.origin;
    } catch {
      return false;
    }
  }
  // In-app relative / root-absolute paths
  return true;
}

function readSearch(): string {
  if (typeof window === 'undefined') return '';
  return window.location.search;
}

function subscribeSearch(onStoreChange: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handler = () => onStoreChange();
  window.addEventListener('popstate', handler);
  return () => window.removeEventListener('popstate', handler);
}

export function useParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): T {
  const pageContext = usePageContext();
  return (pageContext.routeParams ?? {}) as T;
}

export function useLocation() {
  const pageContext = usePageContext();
  const search = useSyncExternalStore(subscribeSearch, readSearch, () => pageContext.urlParsed?.searchOriginal ?? '');
  return {
    pathname: pageContext.urlPathname,
    search: search || pageContext.urlParsed?.searchOriginal || '',
    hash: pageContext.urlParsed?.hash ?? '',
    state: null,
    key: 'default',
  };
}

export function useNavigate() {
  return useCallback((to: NavigationTarget | number, options?: { replace?: boolean }) => {
    if (typeof to === 'number') {
      if (typeof window !== 'undefined') window.history.go(to);
      return;
    }
    clientNavigate(resolveTo(to), { overwriteLastHistoryEntry: options?.replace === true });
  }, []);
}

export function useSearchParams(): [
  URLSearchParams,
  (next: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams), opts?: { replace?: boolean }) => void,
] {
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search.replace(/^\?/, '')), [location.search]);

  const setSearchParams = useCallback(
    (
      next:
        | URLSearchParams
        | Record<string, string>
        | ((prev: URLSearchParams) => URLSearchParams),
      opts?: { replace?: boolean },
    ) => {
      const current = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : location.search);
      let resolved: URLSearchParams;
      if (typeof next === 'function') {
        resolved = next(current);
      } else if (next instanceof URLSearchParams) {
        resolved = next;
      } else {
        resolved = new URLSearchParams(next);
      }
      const search = resolved.toString();
      const pathname = typeof window !== 'undefined' ? window.location.pathname : location.pathname;
      clientNavigate(`${pathname}${search ? `?${search}` : ''}`, {
        overwriteLastHistoryEntry: opts?.replace === true,
      });
    },
    [location.pathname, location.search],
  );

  return [params, setSearchParams];
}

export interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: NavigationTarget;
  replace?: boolean;
  state?: unknown;
  reloadDocument?: boolean;
  preventScrollReset?: boolean;
  relative?: 'route' | 'path';
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

export function Link({ to, children, onClick, replace, reloadDocument, ...rest }: LinkProps) {
  const href = resolveTo(to);
  return (
    <a
      href={href}
      onClick={(event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);
        if (
          event.defaultPrevented ||
          reloadDocument ||
          rest.target === '_blank' ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          !shouldClientNavigate(href)
        ) {
          return;
        }
        event.preventDefault();
        clientNavigate(href, { overwriteLastHistoryEntry: replace === true });
      }}
      {...rest}
    >
      {children}
    </a>
  );
}

export function Navigate({ to, replace = false }: { to: NavigationTarget; replace?: boolean }) {
  const href = resolveTo(to);
  React.useEffect(() => {
    clientNavigate(href, { overwriteLastHistoryEntry: replace });
  }, [href, replace]);
  return null;
}

const OutletContext = createContext<ReactNode>(null);

export function Outlet() {
  return <>{useContext(OutletContext)}</>;
}

/** Used by layouts that previously relied on RR nested <Outlet />. Prefer Vike `children`. */
export function OutletProvider({ children, outlet }: { children: ReactNode; outlet: ReactNode }) {
  return <OutletContext.Provider value={outlet}>{children}</OutletContext.Provider>;
}

/**
 * Synthetic matches from Vike pageContext.
 * Previously LocaleLayout read `handle.bare` via useMatches — now +Layout uses urlLogical,
 * but we still expose bare for any leftover consumers.
 */
export function useMatches(): UIMatch[] {
  const pageContext = usePageContext();
  const pathname = pageContext.urlPathname ?? '/';
  const urlLogical = (pageContext as { urlLogical?: string }).urlLogical ?? pathname;
  const params = (pageContext.routeParams ?? {}) as Record<string, string | undefined>;
  const pageId = (pageContext as { pageId?: string }).pageId ?? 'page';
  const bare = urlLogical.startsWith('/lp/') === true;

  return [
    {
      id: pageId,
      pathname,
      pathnameBase: pathname,
      params,
      data: pageContext.data,
      handle: { bare },
    },
  ];
}

export function useOutlet() {
  return useContext(OutletContext);
}

// Stubs for APIs used only in tests / rare paths — real RR is used under vitest.
export function MemoryRouter({ children }: { children?: ReactNode }) {
  return <>{children}</>;
}

export function BrowserRouter({ children }: { children?: ReactNode }) {
  return <>{children}</>;
}

export function Routes({ children }: { children?: ReactNode }) {
  return <>{children}</>;
}

export function Route(_props: { path?: string; element?: ReactNode; children?: ReactNode }) {
  return null;
}

export function createBrowserRouter(_routes: unknown, _opts?: unknown) {
  if (typeof console !== 'undefined') {
    console.warn(
      '[react-router-shim] createBrowserRouter is a no-op stub under Vike — routing is filesystem-based.',
    );
  }
  return {
    subscribe: () => () => undefined,
    navigate: () => Promise.resolve(),
    state: { location: { pathname: '/', search: '', hash: '', state: null, key: 'default' } },
  };
}

export function RouterProvider({ children }: { children?: ReactNode; router?: unknown }) {
  return <>{children}</>;
}

export type { NavigationTarget };
